import React, { Dispatch, SetStateAction, useState } from "react"
import useT from "utils/useT"
import { motion } from "framer-motion"
import { Form as FormType } from "./types"
import emailjs from "@emailjs/browser"
import { event } from "utils/gtag"
import useSnackbarStore from "stores/snackbar"
import { useGoogleReCaptcha } from "react-google-recaptcha-v3"
import { useMutation } from "@tanstack/react-query"

type Props = {
  setSuccess: Dispatch<SetStateAction<boolean>>
}

const FormError = ({ errorMessage }: { errorMessage: string }) => {
  return (
    <motion.div
      initial={{ height: 0 }}
      animate={{ height: "auto" }}
      exit={{ height: 0 }}
      className="color-red-dark font-13 mt-n3 mb-2"
    >
      {errorMessage}
    </motion.div>
  )
}

const Form = ({ setSuccess }: Props) => {
  const t = useT()
  const [loading, setPending] = useState(false)
  const [form, setForm] = useState<FormType>({
    name: "",
    email: "",
    message: "",
    nameError: "",
    emailError: "",
    messageError: "",
    captchaError: "",
  })
  const toggleToast = useSnackbarStore((state) => state.toggleToast)
  const { executeRecaptcha } = useGoogleReCaptcha()

  const { mutateAsync } = useMutation({
    mutationFn: (gReCaptchaToken: string) =>
      fetch("/api/recaptcha", {
        method: "POST",
        headers: {
          Accept: "application/json, text/plain, */*",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          gRecaptchaToken: gReCaptchaToken,
        }),
      }).then((res) => res.json()),
  })

  const nameError = form.name.split(" ").length < 2
  const emailError = !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(
    form.email,
  )
  const captchaError = form.captchaError
  const messageError = form.message.length < 1
  const checkErrors = async () => {
    return new Promise(async (res) => {
      setForm((prevState) => ({
        ...prevState,
        emailError: "",
        nameError: "",
        messageError: "",
        captchaError: "",
      }))
      if (nameError) {
        setForm((prevState) => ({
          ...prevState,
          nameError: t.contact.nameError,
        }))
      }
      if (emailError) {
        setForm((prevState) => ({
          ...prevState,
          emailError: t.contact.emailError,
        }))
      }
      if (messageError) {
        setForm((prevState) => ({
          ...prevState,
          messageError: t.contact.messageError,
        }))
      }
      if (nameError || emailError || messageError) {
        res(false)
        return
      }
      if (!executeRecaptcha) {
        console.log("Execute recaptcha not yet available")
        setForm((prevState) => ({
          ...prevState,
          captchaError: t.contact.captchaError,
        }))
        res(false)
        return
      }
      setPending(true)
      const gReCaptchaToken = await executeRecaptcha("contactFormSubmit")

      const data = await mutateAsync(gReCaptchaToken)

      if (!data.success) {
        setForm((prevState) => ({
          ...prevState,
          captchaError: t.contact.captchaError,
        }))
        setPending(false)
        res(false)
      }
      res(true)
    })
  }

  const submit = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault()

    const isSuccess = await checkErrors()

    if (nameError || emailError || messageError || captchaError || !isSuccess)
      return
    setPending(true)

    emailjs
      .send(
        "service_oc8qznf",
        "template_oc0q9be",
        {
          rnEmail: form.email,
          rnSubject: "Appollow contact form",
          rnMessage: form.message,
          rnName: form.name,
        },
        "user_HDJ1SCLZ6I6ByO7Ey8D36",
      )
      .then(() => {
        setSuccess((prevState: boolean) => !prevState)
        event({
          action: "contact_form_submit",
          category: "contact",
          label: "Contact form submit",
          value: "Contact form submit",
        })
        setForm((prevState) => ({
          ...prevState,
          captcha: "",
        }))
        setPending(false)
      })
      .catch((err) => {
        setPending(false)
        console.log("Failed to send feedback. Error: ", err)

        toggleToast({
          text: t.snackbar.error,
          color: "red",
        })
      })
  }
  return (
    <motion.div
      layoutId="contact-form"
      className="card card-style contact-form"
    >
      <div className="content">
        <form className="contactForm" id="contactForm">
          <fieldset>
            <div className="form-field form-name border-color-red">
              <label
                className="contactNameField color-theme"
                htmlFor="contactNameField"
              >
                {t.contact.name}
                <span>({t.contact.required})</span>
              </label>
              <input
                type="text"
                name="contactNameField"
                value={form.name}
                onChange={(e) =>
                  setForm({ ...form, name: e.target.value, nameError: "" })
                }
                className="round-small"
                id="contactNameField"
              />
              {form.nameError && <FormError errorMessage={form.nameError} />}
            </div>
            <div className="form-field form-email">
              <label
                className="contactEmailField color-theme"
                htmlFor="contactEmailField"
              >
                {t.contact.email}
                <span>({t.contact.required})</span>
              </label>
              <input
                type="text"
                name="contactEmailField"
                value={form.email}
                onChange={(e) =>
                  setForm({ ...form, email: e.target.value, emailError: "" })
                }
                className="round-small"
                id="contactEmailField"
              />
              {form.emailError && <FormError errorMessage={form.emailError} />}
            </div>
            <div className="form-field form-text">
              <label
                className="contactMessageTextarea color-theme"
                htmlFor="contactMessageTextarea"
              >
                {t.contact.message}
                <span>({t.contact.required})</span>
              </label>
              <textarea
                value={form.message}
                onChange={(e) =>
                  setForm({
                    ...form,
                    message: e.target.value,
                    messageError: "",
                  })
                }
                name="contactMessageTextarea"
                className="round-small mb-2"
                id="contactMessageTextarea"
              ></textarea>
              {form.messageError && (
                <FormError errorMessage={form.messageError} />
              )}
            </div>
            {form.captchaError && (
              <div className="text-center">
                <FormError errorMessage={form.captchaError} />
              </div>
            )}
            <div className="form-button">
              <motion.button
                onClick={(e) => submit(e)}
                type="submit"
                className="btn bg-highlight text-uppercase font-900 btn-m btn-full rounded-sm  shadow-xl contactSubmitButton"
              >
                {t.contact.sendMessage}
                {loading ? (
                  <div className="spinner-border spinner-border-sm ms-2"></div>
                ) : null}
              </motion.button>
            </div>
          </fieldset>
        </form>
      </div>
    </motion.div>
  )
}

export default Form
